import { createBaselineProgress } from './catalog';
import type { PilotLearnerRecord, PilotMathState, PilotSkillProgress } from './types';

const STORAGE_KEY = 'belajarku:pilot-math:learner:v1';
export const PILOT_LEARNER_UPDATED_EVENT = 'belajarku:pilot-math:learner-updated';

const demoStudent: PilotLearnerRecord['student'] = { id: 'demo-student-andi', displayName: 'Andi' };

function createRecord(progress?: Record<string, PilotSkillProgress>): PilotLearnerRecord {
  return {
    schemaVersion: 1,
    student: demoStudent,
    progress: progress ?? createBaselineProgress(),
    days: [],
    updatedAt: new Date().toISOString(),
  };
}

export function loadPilotLearnerRecord(): PilotLearnerRecord {
  if (typeof window === 'undefined') return createRecord();
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    const record = createRecord();
    savePilotLearnerRecord(record);
    return record;
  }
  try {
    const parsed = JSON.parse(raw) as PilotLearnerRecord;
    if (parsed.schemaVersion !== 1 || !parsed.progress || !Array.isArray(parsed.days)) throw new Error('Unsupported learner record');
    return parsed;
  } catch {
    const record = createRecord();
    savePilotLearnerRecord(record);
    return record;
  }
}

export function savePilotLearnerRecord(record: PilotLearnerRecord) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent(PILOT_LEARNER_UPDATED_EVENT));
}

export function adoptLegacyProgress(progress: Record<string, PilotSkillProgress>) {
  const current = loadPilotLearnerRecord();
  if (current.days.length > 0 || Object.values(current.progress).some((skill) => skill.evidenceCount > 0)) return;
  savePilotLearnerRecord({ ...current, progress, updatedAt: new Date().toISOString() });
}

export function syncLearnerRecordFromDailyState(state: PilotMathState) {
  const current = loadPilotLearnerRecord();
  const day = {
    learningDate: state.learningDate,
    assignmentId: state.assignmentId,
    status: state.status,
    ...(state.startedAt ? { startedAt: state.startedAt } : {}),
    ...(state.completedAt ? { completedAt: state.completedAt } : {}),
    attempts: state.attempts,
  };
  const days = [day, ...current.days.filter((item) => item.learningDate !== state.learningDate)]
    .sort((a, b) => b.learningDate.localeCompare(a.learningDate))
    .slice(0, 180);
  savePilotLearnerRecord({
    ...current,
    student: state.student,
    progress: state.progress,
    days,
    updatedAt: new Date().toISOString(),
  });
}

export function resetPilotLearnerRecord() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(PILOT_LEARNER_UPDATED_EVENT));
}
